import React, { createContext, useContext, useState, useCallback } from 'react';
import { DocumentChatContextType, DocumentChatMessage } from '@/types/documentChat';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';

const DocumentChatContext = createContext<DocumentChatContextType>({
  messages: [],
  isLoading: false,
  isTyping: false,
  error: null,
  currentDocumentId: null,
  sendMessage: async () => {},
  loadMessages: async () => {},
  clearMessages: () => {},
  setError: () => {},
  setActiveDocument: () => {},
});

export const useDocumentChat = () => useContext(DocumentChatContext);

interface DocumentChatProviderProps {
  children: React.ReactNode;
}

export const DocumentChatProvider: React.FC<DocumentChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<DocumentChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [activeDocumentContent, setActiveDocumentContent] = useState<string>('');
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);

  // [DIAGNÓSTICO] debugId único para rastrear provider
  const debugId = React.useMemo(
    () => `DocChatProvider@${Math.random().toString(36).slice(2,8)}`,
    []
  );

  // [DIAGNÓSTICO] Flag via URL
  const dbgTyping = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('dbgTyping');

  // [DIAGNÓSTICO] Função para forçar estado isTyping
  const debugForceTyping = useCallback((ms = 3000) => {
    if (!dbgTyping) return;
    console.log('[DOC-CHAT][DEBUG] Forçando isTyping=true por', ms, 'ms');
    setIsTyping(true);
    setTimeout(() => {
      console.log('[DOC-CHAT][DEBUG] Forçando isTyping=false');
      setIsTyping(false);
    }, ms);
  }, [dbgTyping]);

  const loadMessages = useCallback(async (documentId: string) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setCurrentDocumentId(documentId);
      
      const { data, error } = await supabase
        .from('document_chats')
        .select('*')
        .eq('document_id', documentId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro ao carregar mensagens do documento:', error);
        setError('Erro ao carregar mensagens');
        return;
      }

      const formattedMessages: DocumentChatMessage[] = (data || []).map(msg => ({
        ...msg,
        role: msg.role as 'user' | 'assistant',
        timestamp: new Date(msg.created_at)
      }));

      setMessages(formattedMessages);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setError('Erro ao carregar mensagens');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const sendMessage = useCallback(async (content: string, documentId: string) => {
    if (!user || !content.trim()) return;

    // ⏰ Timeout de segurança: se demorar > 30s, reseta isTyping
    const timeoutId = setTimeout(() => {
      console.error('⏰ TIMEOUT: resetando isTyping após 30s');
      setIsTyping(false);
    }, 30000);

    const userMessage: DocumentChatMessage = {
      id: crypto.randomUUID(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date(),
      document_id: documentId,
      user_id: user.id
    };

    try {
      // Adiciona mensagem do usuário localmente
      setMessages(prev => [...prev, userMessage]);
      setIsTyping(true);
      setError(null);

      // Salva mensagem do usuário no banco
      const { error: saveError } = await supabase
        .from('document_chats')
        .insert({
          content: userMessage.content,
          role: userMessage.role,
          document_id: documentId,
          user_id: user.id
        });

      if (saveError) {
        console.error('Erro ao salvar mensagem do usuário:', saveError);
      }

      // [DIAGNÓSTICO] Mock de latência controlada
      if (dbgTyping) {
        console.log('[DOC-CHAT][DEBUG] Mock: aguardando 800ms antes de chamar IA...');
        await new Promise(r => setTimeout(r, 800));
      }

      // Chama Edge Function para resposta da IA
      const { data: aiResponse, error: aiError } = await supabase.functions.invoke('document-chat', {
        body: {
          message: content,
          documentId, // ID do CASO
          activeDocumentId: activeDocumentId || documentId, // ID do documento específico
          documentContent: activeDocumentContent, // Conteúdo do documento
          userId: user.id,
          conversationHistory: messages.slice(-10), // Últimas 10 mensagens para contexto
          kb: {
            enabled: true, // Ativar Base de Conhecimento
            topK: 6 // Buscar 6 trechos relevantes
          }
        }
      });

      if (aiError) {
        console.error('Erro na resposta da IA:', aiError);
        setError('Erro ao obter resposta da IA');
        return;
      }

      // Adiciona resposta da IA
      const assistantMessage: DocumentChatMessage = {
        id: crypto.randomUUID(),
        content: aiResponse.response || 'Desculpe, não consegui processar sua mensagem.',
        role: 'assistant',
        timestamp: new Date(),
        document_id: documentId,
        user_id: user.id
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Salva resposta da IA no banco
      const { error: saveAiError } = await supabase
        .from('document_chats')
        .insert({
          content: assistantMessage.content,
          role: assistantMessage.role,
          document_id: documentId,
          user_id: user.id
        });

      if (saveAiError) {
        console.error('Erro ao salvar resposta da IA:', saveAiError);
      }

    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem');
    } finally {
      clearTimeout(timeoutId);
      setIsTyping(false);
    }
  }, [user, messages, activeDocumentId, activeDocumentContent]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setCurrentDocumentId(null);
    setError(null);
  }, []);

  const setActiveDocument = useCallback((docId: string | null, content: string) => {
    setActiveDocumentId(docId);
    setActiveDocumentContent(content);
  }, []);

  // [DIAGNÓSTICO] Expor debugForceTyping globalmente
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && dbgTyping) {
      (window as any).__docChatDebug = { debugForceTyping };
      console.log('[DOC-CHAT][DEBUG] window.__docChatDebug.debugForceTyping() disponível');
    }
  }, [debugForceTyping, dbgTyping]);

  // [DIAGNÓSTICO] Context value com TypeScript seguro
  const baseValue: DocumentChatContextType = {
    messages,
    isLoading,
    isTyping,
    error,
    currentDocumentId,
    sendMessage,
    loadMessages,
    clearMessages,
    setError,
    setActiveDocument
  };

  const devExtra =
    process.env.NODE_ENV !== 'production' && dbgTyping
      ? { debugId, debugForceTyping }
      : {};

  const contextValue = { ...baseValue, ...(devExtra as Record<string, unknown>) };

  return (
    <DocumentChatContext.Provider value={contextValue}>
      {children}
    </DocumentChatContext.Provider>
  );
};